import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CACHE_KEY = 'bcm_site_settings';
const CACHE_VERSION_KEY = 'bcm_cache_ver';
const CACHE_VERSION = '3';

// Default settings
const defaultSettings = {
  businessName: 'Big Chief Motors',
  tagline: 'Your Trusted Local Dealership',
  phone: '1-800-985-2117',
  email: 'info@bigchiefmotors.com',
  address: '4-2071 Plessis Rd, Winnipeg, MB R5R 0C8',
  hours: 'Mon-Fri: 9AM-7PM | Sat: 10AM-5PM | Sun: Closed',
  heroTitle: 'Find Your Perfect Vehicle Today',
  heroSubtitle: 'At Big Chief Motors, we offer a handpicked selection of trucks, SUVs, sedans, and more — quality vehicles at fair prices with no hidden fees.',
  heroImage: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800&q=80',
  aboutText: 'At Big Chief Motors, we make buying a vehicle simple, transparent, and stress-free.',
  facebookUrl: '',
  instagramUrl: '',
  twitterUrl: '',
  stat1Value: '500+',
  stat1Label: 'Happy Customers',
  stat2Value: '100%',
  stat2Label: 'No Hidden Fees',
  stat3Value: 'Fast',
  stat3Label: 'Easy Financing',
  showHero: true,
  showWhyChooseUs: true,
  showInventory: true,
  showHappyCustomers: true,
  showTestimonials: true,
  showFinancing: true,
  bannerEnabled: false,
  bannerText: '',
  bannerLink: '',
  bannerColor: '#dc2626',
  // Analytics & tracking (edited in Admin → Features → Analytics & Tracking)
  ga4MeasurementId: '',
  googleAdsId: '',
  googleAdsLeadLabel: '',
  metaPixelId: '',
  cookieBannerEnabled: true,
  googleStoreCode: ''
};

// Load cached settings from localStorage
const getCachedSettings = () => {
  try {
    const ver = localStorage.getItem(CACHE_VERSION_KEY);
    if (ver !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY);
      localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
      return defaultSettings;
    }
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return { ...defaultSettings, ...JSON.parse(cached) };
  } catch (e) {}
  return defaultSettings;
};

const SiteSettingsContext = createContext(defaultSettings);

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(getCachedSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        const merged = { ...defaultSettings, ...response.data };
        setSettings(merged);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(response.data)); } catch (e) {}
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    await fetchSettings();
  };

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    return { settings: defaultSettings, loading: false, refreshSettings: () => {} };
  }
  return context;
};

export default SiteSettingsContext;
